import { Button, Table, Text, UnstyledButton } from '@mantine/core';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { SortDirection, formatDateTime } from '@feature/utils';

import { UserDocumentsRow, userDocumentKindList } from '../model/UserDocumentTypes';

interface UserDocumentCompletenessTableProps {
  rows: UserDocumentsRow[];
  sortDirection: SortDirection;
  toggleSortDirection: () => void;
  onOpenUser: (row: UserDocumentsRow) => void;
}

function SlotCell({ row, kind }: { row: UserDocumentsRow; kind: string }) {
  const document = row.slots.find(slot => slot.kind === kind)?.document ?? null;

  if (document == null) {
    return (
      <Text
        size="xs"
        c="dimmed"
      >
        -
      </Text>
    );
  }

  return <Text size="xs">{formatDateTime(document.uploadedAt)}</Text>;
}

export function UserDocumentCompletenessTable(props: UserDocumentCompletenessTableProps) {
  const { rows, sortDirection, toggleSortDirection, onOpenUser } = props;
  const { t } = useTranslation();

  return (
    <Table.ScrollContainer minWidth={900}>
      <Table
        striped
        highlightOnHover
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <UnstyledButton
                onClick={toggleSortDirection}
                fz="sm"
              >
                {t('userDocuments.table.name')}{' '}
                {sortDirection === 'ASC' ? <IconArrowUp size={12} /> : <IconArrowDown size={12} />}
              </UnstyledButton>
            </Table.Th>
            <Table.Th>{t('userDocuments.table.faculty')}</Table.Th>
            <Table.Th>{t('userDocuments.table.department')}</Table.Th>
            <Table.Th>{t('userDocuments.table.discipline')}</Table.Th>
            {userDocumentKindList.map(kind => (
              <Table.Th key={kind}>{t(`userDocuments.kindEnum.${kind}`)}</Table.Th>
            ))}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(row => (
            <Table.Tr key={row.user.id}>
              <Table.Td>{row.user.name ?? `#${row.user.id}`}</Table.Td>
              <Table.Td>{row.faculty?.name ?? '-'}</Table.Td>
              <Table.Td>{row.department?.name ?? '-'}</Table.Td>
              <Table.Td>{row.discipline?.name ?? '-'}</Table.Td>
              {userDocumentKindList.map(kind => (
                <Table.Td key={kind}>
                  <SlotCell
                    row={row}
                    kind={kind}
                  />
                </Table.Td>
              ))}
              <Table.Td>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => onOpenUser(row)}
                >
                  {t('userDocuments.table.manage')}
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
